const { qs, qsa, accept, build } = require('../../libs/qs')
const uuid = require('uuid').v4
const ctrlz = require('../common/ctrlz')
const { faderH, dualrange } = ctrlz
const engine = require('../common/engine')
const fxs = require('../common/fx')
const Sortable = require('sortablejs')

// ---- Bucket integration ----
const initLegacyBucket = require('./legacy-bucket-integration.js');

class ImageProcessor {
    constructor() {
        this.worker = null;
        this.ready = false;
        this.pendingRequests = new Map();
        this.requestId = 0;
        this.isWorkerSupported = typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined';
    }

    _createWorker() {
        const blob = new Blob([__WORKER_SOURCE__], { type: 'application/javascript' });
        const workerUrl = URL.createObjectURL(blob);
        this.worker = new Worker(workerUrl);
        this.worker.onmessage = (e) => this.handleMessage(e.data);
        this.worker.onerror = () => {
            this.ready = false;
        };
    }

    _waitUntilReady(maxAttempts = 100) {
        return new Promise((resolve) => {
            let attempts = 0;
            const checkReady = () => {
                if (this.ready) {
                    resolve(true);
                    return;
                }
                attempts++;
                if (attempts > maxAttempts) {
                    resolve(false);
                    return;
                }
                setTimeout(checkReady, 50);
            };
            checkReady();
        });
    }

    async init() {
        if (!this.isWorkerSupported) return false;
        return new Promise((resolve) => {
            try {
                this._createWorker();
                this._waitUntilReady().then(resolve);
            } catch (error) {
                resolve(false);
            }
        });
    }

    pendingCount() {
        return this.pendingRequests.size;
    }

    async cancelCurrentPipeline(reason = 'Pipeline cancelled by new request') {
        this.pendingRequests.forEach(({ reject }) => {
            try {
                reject(new Error(reason));
            } catch (_) {
            }
        });
        this.pendingRequests.clear();

        if (this.worker) {
            try {
                this.worker.terminate();
            } catch (_) {
            }
            this.worker = null;
        }

        this.ready = false;

        if (!this.isWorkerSupported) {
            return false;
        }

        try {
            this._createWorker();
            this.ready = await this._waitUntilReady();
        } catch (_) {
            this.ready = false;
        }

        return this.ready;
    }

    handleMessage(data) {
        const { type, id, success, data: resultData, error, width, height } = data;
        if (type === 'ready') {
            this.ready = true;
            return;
        }
        if ((type === 'fxResult' || type === 'batchResult') && id !== undefined) {
            const pending = this.pendingRequests.get(id);
            if (pending) {
                if (success) pending.resolve({ data: resultData, width, height });
                else pending.reject(new Error(error));
                this.pendingRequests.delete(id);
            }
        }
    }

    async processFx(fxName, imageData, width, height, ...args) {
        if (!this.ready) throw new Error('Worker not ready');
        const id = ++this.requestId;
        const canvasData = new Uint8ClampedArray(imageData);
        return new Promise((resolve, reject) => {
            this.pendingRequests.set(id, { resolve, reject });
            this.worker.postMessage({ type: 'processFx', id, payload: { fxName, canvasData: canvasData.buffer, width, height, args } }, [canvasData.buffer]);
            setTimeout(() => {
                if (this.pendingRequests.has(id)) {
                    this.pendingRequests.delete(id);
                    reject(new Error(`Timeout: ${fxName}`));
                }
            }, 120000);
        });
    }

    async processBatch(fxList, imageData, width, height) {
        if (!this.ready) throw new Error('Worker not ready');
        const id = ++this.requestId;
        const canvasData = new Uint8ClampedArray(imageData);
        return new Promise((resolve, reject) => {
            this.pendingRequests.set(id, { resolve, reject });
            this.worker.postMessage({ type: 'processBatch', id, payload: { fxList, canvasData: canvasData.buffer, width, height } }, [canvasData.buffer]);
            setTimeout(() => {
                if (this.pendingRequests.has(id)) {
                    this.pendingRequests.delete(id);
                    reject(new Error(`Batch timeout`));
                }
            }, 120000);
        });
    }

    isReady() {
        return this.ready;
    }
}

window.ImageProcessor = ImageProcessor;
engine.initWorker()

// Use localStorage theme if available, otherwise fall back to server-provided window.Theme
let currentTheme;
try {
  const storedTheme = localStorage.getItem('currentTheme');
  if (storedTheme) {
    currentTheme = JSON.parse(storedTheme);
  }
} catch (e) {}
if (!currentTheme) {
  currentTheme = window.Theme;
}

const themeColors = Object.values(currentTheme.data)
const themeRgb = themeColors.map((el) => {
    // convert each color to rgb [{r:0,g:0,b:0},{r:0,g:0,b:0}]
    return { r: parseInt(el.slice(1, 3), 16), g: parseInt(el.slice(3, 5), 16), b: parseInt(el.slice(5, 7), 16) }
})

const processingIndicator = { icon: '|', frames: ['|', '/', '-', '\\'], frameIndex: 0, interval: null }
let isFxProcessing = false
const UPLOAD_HASH_STORAGE_KEY = 'unstaglitch_uploaded_hashes_v1'
const uploadedImageHashes = new Set()

let stopTimeout = null

try {
    const rawHashes = localStorage.getItem(UPLOAD_HASH_STORAGE_KEY)
    if (rawHashes) {
        const parsed = JSON.parse(rawHashes)
        if (Array.isArray(parsed)) {
            parsed.forEach((hash) => {
                if (typeof hash === 'string' && hash) {
                    uploadedImageHashes.add(hash)
                }
            })
        }
    }
} catch (_) {
}

const persistUploadedHashes = () => {
    try {
        const max = 300
        const values = Array.from(uploadedImageHashes)
        const trimmed = values.length > max ? values.slice(values.length - max) : values
        localStorage.setItem(UPLOAD_HASH_STORAGE_KEY, JSON.stringify(trimmed))
    } catch (_) {
    }
}

const fallbackHash = (input) => {
    let hash = 2166136261
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i)
        hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)
    }
    return `f${(hash >>> 0).toString(16)}`
}

const hashImageData = async (input) => {
    try {
        if (window.crypto && window.crypto.subtle && typeof TextEncoder !== 'undefined') {
            const encoded = new TextEncoder().encode(input)
            const digest = await window.crypto.subtle.digest('SHA-256', encoded)
            return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('')
        }
    } catch (_) {
    }
    return fallbackHash(input)
}

const ensureToastContainer = () => {
    let container = document.getElementById('ug-toast-container')
    if (!container) {
        container = document.createElement('div')
        container.id = 'ug-toast-container'
        container.className = 'ug-toast-container'
        document.body.appendChild(container)
    }
    return container
}

const showTransientToast = (message) => {
    const container = ensureToastContainer()
    const toast = document.createElement('div')
    toast.className = 'ug-toast ug-toast-success'
    toast.textContent = String(message || 'Upload succeeded')
    container.appendChild(toast)

    setTimeout(() => {
        toast.classList.add('ug-toast-show')
    }, 10)

    setTimeout(() => {
        toast.classList.remove('ug-toast-show')
        setTimeout(() => toast.remove(), 220)
    }, 1800)
}

function stopProcessingAnimation() {
    if (stopTimeout) return
    stopTimeout = setTimeout(() => {
        isFxProcessing = false
        if (processingIndicator.interval) {
            clearInterval(processingIndicator.interval)
            processingIndicator.interval = null
        }
        const fxProcessingEl = qs('#fx-processing')
        if (fxProcessingEl) fxProcessingEl.style.display = 'none'
        const outcanvas = qs('#outputcanvas')
        if (outcanvas) outcanvas.style.opacity = '1'
        const storeBtn = qs('#store')
        if (storeBtn) storeBtn.disabled = false
        stopTimeout = null
    }, 200)
}

function showProcessingNow() {
    if (stopTimeout) {
        clearTimeout(stopTimeout)
        stopTimeout = null
    }
    isFxProcessing = true
    const fxProcessingEl = qs('#fx-processing')
    if (fxProcessingEl) fxProcessingEl.style.display = 'block'
    const outcanvas = qs('#outputcanvas')
    if (outcanvas) outcanvas.style.opacity = '0.4'
    const storeBtn = qs('#store')
    if (storeBtn) storeBtn.disabled = true
    if (!processingIndicator.interval) {
        processingIndicator.interval = setInterval(() => {
            if (fxProcessingEl) {
                fxProcessingEl.textContent = processingIndicator.frames[processingIndicator.frameIndex]
                processingIndicator.frameIndex = (processingIndicator.frameIndex + 1) % processingIndicator.frames.length
            }
        }, 100)
    }
}

engine.on('start', () => showProcessingNow())
engine.on('complete', () => stopProcessingAnimation())
engine.on('error', () => showTransientToast('FX processing failed'))


window.CONTROLZ = {}
window.themeColors = themeColors
window.currentTheme = currentTheme
window.themeRgb = themeRgb
window.lfoz = {}
window.lfoindex = 0
window.lfolinks = {}
window.bandlinks = {}
window.macrolinks = {}
window.midilinks = {}

document.addEventListener('DOMContentLoaded', () => {
 
        const store = qs('#store')
        if (store) {
            ctrlz.setStore(store)
        }

        const dflt=["robot","cyber","cyberpunk","glitch","cyber","sci-fi","8bit","16bit","future","electronics","pcb","buildings","architecture","destroy","motherboard","circuits","droid","noise","code","sourcecode"]

        qs('#searchinpt').value=dflt[Math.floor(Math.random() * dflt.length)]
     
        function debounce(func, wait, immediate) {
            var timeout;
            return function () {
                var context = this, args = arguments;
                var later = function () {
                    timeout = null;
                    if (!immediate) func.apply(context, args);
                };
                var callNow = immediate && !timeout;
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
                if (callNow) func.apply(context, args);
            };
        };
        

        fxs.forEach((fx, idx) => {

            const option = build('li')
            option.innerHTML = fx.name

            option.addEventListener('click', () => {
                return buildctrl(fx)
            })

            qs('#fxlist').appendChild(option)
        })

        qs('#random-fx-btn')?.addEventListener('click', () => {
            const idx = Math.floor(Math.random() * fxs.length)
            buildctrl(fxs[idx])
        })

        let sortable

        const originalimg = build('img')
        const stageimg = build('img')
        let outcanvas = qs('#outputcanvas')
        let outctx = outcanvas.getContext("2d")
        const resolutionSelect = qs('#resolution')
        const sourceSelect = qs('#searchsource')
        const searchResults = qs('#searchresults')
        const wallhavenControls = qs('#wallhaven-controls')
        let loading = false
        let currentResolution = Number(resolutionSelect?.value || 600)
        let searchController = null
        let searchRequestId = 0
        let igscrapLastCallAt = 0
        const IGSCRAP_DEBOUNCE_MS = 450
        const IGSTORE_TARGET_MAX_BYTES = 28 * 1024 * 1024
        const IGSTORE_MIN_SIDE = 1080

        const waitForIgscrapSlot = (signal) => {
            const elapsed = Date.now() - igscrapLastCallAt
            const delay = Math.max(0, IGSCRAP_DEBOUNCE_MS - elapsed)

            if (!delay) {
                return Promise.resolve()
            }

            return new Promise((resolve, reject) => {
                const timer = setTimeout(() => {
                    if (signal) {
                        signal.removeEventListener('abort', onAbort)
                    }
                    resolve()
                }, delay)

                const onAbort = () => {
                    clearTimeout(timer)
                    signal.removeEventListener('abort', onAbort)
                    reject(new DOMException('Aborted', 'AbortError'))
                }

                if (signal) {
                    signal.addEventListener('abort', onAbort, { once: true })
                }
            })
        }

        const fetchIgscrapDebounced = async (url, options = {}) => {
            if (options?.signal?.aborted) {
                throw new DOMException('Aborted', 'AbortError')
            }

            await waitForIgscrapSlot(options?.signal)
            igscrapLastCallAt = Date.now()
            return fetch(url, options)
        }

        const estimateDataUrlBytes = (dataUrl) => {
            const commaIdx = dataUrl.indexOf(',')
            if (commaIdx < 0) {
                return dataUrl.length
            }
            const base64Len = dataUrl.length - commaIdx - 1
            const padding = dataUrl.endsWith('==') ? 2 : (dataUrl.endsWith('=') ? 1 : 0)
            return Math.floor((base64Len * 3) / 4) - padding
        }

        const buildUploadPngDataUrl = (sourceCanvas) => {
            let workCanvas = build('canvas')
            let workCtx = workCanvas.getContext('2d')
            workCanvas.width = sourceCanvas.width
            workCanvas.height = sourceCanvas.height
            workCtx.drawImage(sourceCanvas, 0, 0)

            let dataUrl = workCanvas.toDataURL('image/png')
            let bytes = estimateDataUrlBytes(dataUrl)

            while (bytes > IGSTORE_TARGET_MAX_BYTES && Math.min(workCanvas.width, workCanvas.height) > IGSTORE_MIN_SIDE) {
                const nextCanvas = build('canvas')
                const nextCtx = nextCanvas.getContext('2d')
                nextCanvas.width = Math.max(IGSTORE_MIN_SIDE, Math.floor(workCanvas.width * 0.85))
                nextCanvas.height = Math.max(IGSTORE_MIN_SIDE, Math.floor(workCanvas.height * 0.85))
                nextCtx.drawImage(workCanvas, 0, 0, nextCanvas.width, nextCanvas.height)
                workCanvas = nextCanvas
                workCtx = nextCtx
                dataUrl = workCanvas.toDataURL('image/png')
                bytes = estimateDataUrlBytes(dataUrl)
            }

            return {
                dataUrl,
                bytes,
                width: workCanvas.width,
                height: workCanvas.height,
                resized: workCanvas.width !== sourceCanvas.width || workCanvas.height !== sourceCanvas.height
            }
        }

        let wallhavenPage = 1
        let wallhavenMeta = null
        let wallhavenLoading = false
        let wallhavenObserver = null

        // Resolution mapping with width and height for different aspect ratios
        const resolutionMap = {
            600: { width: 600, height: 600 },      // Square
            1200: { width: 1200, height: 1200 },    // Square
            1800: { width: 1800, height: 1800 },    // Square
            2400: { width: 2400, height: 2400 },    // Square
            1920: { width: 1920, height: 1080 },    // 16:9 wallpaper
            2560: { width: 2560, height: 1440 },    // 16:9 wallpaper
            3840: { width: 3840, height: 2160 },    // 16:9 wallpaper
            1080: { width: 1080, height: 1350 },    // 4:5 Instagram portrait
            1500: { width: 1200, height: 1500 },    // 4:5 Instagram portrait
            750: { width: 600, height: 750 }         // 4:5 Instagram portrait
        }

        const getCurrentResolution = () => {
            const resValue = resolutionSelect?.value || 'original'
            if (resValue === 'original' && originalimg.complete && originalimg.width && originalimg.height) {
                return { width: originalimg.width, height: originalimg.height }
            }
            const numValue = Number(resValue)
            return resolutionMap[numValue] || { width: numValue, height: numValue }
        }

        // Initialize canvas with default size
        const initResolution = getCurrentResolution()
        outcanvas.width = initResolution.width
        outcanvas.height = initResolution.height
        outctx.fillStyle = currentTheme.data.c1
        outctx.fillRect(0, 0, outcanvas.width, outcanvas.height)
        outctx.font = 'bold 24px bit'
        outctx.fillStyle = currentTheme.data.cf
        outctx.textAlign = 'center'
        outctx.fillText('Load an image to start', outcanvas.width / 2, outcanvas.height / 2)

        const hasFxApplied = () => qsa('#fxbox .fxhold').length > 0

        const drawStagePlaceholder = () => {
            const res = getCurrentResolution()
            outcanvas.width = res.width
            outcanvas.height = res.height
            outctx.fillStyle = currentTheme.data.c1
            outctx.fillRect(0, 0, outcanvas.width, outcanvas.height)
            outctx.font = 'bold 24px bit'
            outctx.fillStyle = currentTheme.data.cf
            outctx.textAlign = 'center'
            outctx.fillText('Load an image to start', outcanvas.width / 2, outcanvas.height / 2)
        }

        const renderStageImage = () => {
            if (!stageimg.complete || !stageimg.width || !stageimg.height) {
                return false
            }
            const res = getCurrentResolution()
            outcanvas.width = res.width
            outcanvas.height = res.height
            engine.Draw(outctx, stageimg)
            return true
        }

        const debouncedRenderStageImage = debounce(() => {
            renderStageImage()
        }, 300)

        const normalizeToTargetResolution = (img) => {
            const canvas = build('canvas')
            const ctx = canvas.getContext('2d')
            const res = getCurrentResolution()
            canvas.width = res.width
            canvas.height = res.height
            ctx.fillStyle = currentTheme.data.c1
            ctx.fillRect(0, 0, res.width, res.height)
            ctx.drawImage(img, 0, 0, res.width, res.height)
            return canvas.toDataURL('image/png')
        }

        stageimg.onload = function () {
            renderStageImage()
        }

        const debouncedResolutionChange = debounce(() => {
            if (originalimg.complete && originalimg.width && originalimg.height) {
                stageimg.src = normalizeToTargetResolution(originalimg)
            } else {
                drawStagePlaceholder()
            }
        }, 300)

        if (resolutionSelect) {
            resolutionSelect.addEventListener('change', () => {
                debouncedResolutionChange()
            })
        }

        async function loadImg(src, fallbackSrc) {
            let fallbackTried = false

            originalimg.onload = function () {
                originalimg.onerror = null
                resolutionSelect.value = 'original'
                stageimg.src = normalizeToTargetResolution(originalimg)
            }

            originalimg.onerror = function () {
                if (!fallbackTried && fallbackSrc && fallbackSrc !== src) {
                    fallbackTried = true
                    originalimg.src = '/api/igserve/' + btoa(fallbackSrc)
                    return
                }
                originalimg.onerror = null
            }

            originalimg.src = '/api/igserve/' + btoa(src)
        }

        const renderSearchMessage = (message, className = 'searchresults-status') => {
            searchResults.innerHTML = ''
            const status = build('div')
            status.className = className
            status.textContent = message
            searchResults.appendChild(status)
        }

        const renderSearchLoading = () => {
            searchResults.innerHTML = ''
            const loadingBox = build('div')
            const spinner = build('span')
            const label = build('span')
            loadingBox.className = 'searchresults-status searchresults-loading'
            spinner.className = 'searchresults-spinner'
            label.textContent = ' loading'
            loadingBox.appendChild(spinner)
            loadingBox.appendChild(label)
            searchResults.appendChild(loadingBox)
        }

        const getWallhavenParams = () => {
            const catGeneral = qs('#wh-cat-general')?.checked ? '1' : '0'
            const catAnime = qs('#wh-cat-anime')?.checked ? '1' : '0'
            const catPeople = qs('#wh-cat-people')?.checked ? '1' : '0'
            const categories = catGeneral + catAnime + catPeople

            const purSfw = qs('#wh-pur-sfw')?.checked ? '1' : '0'
            const purSketchy = qs('#wh-pur-sketchy')?.checked ? '1' : '0'
            const purNsfw = qs('#wh-pur-nsfw')?.checked ? '1' : '0'
            const purity = purSfw + purSketchy + purNsfw

            const sorting = qs('#wh-sorting')?.value || 'relevance'
            const order = qs('#wh-order')?.value || 'desc'
            const toprange = qs('#wh-toprange')?.value || '1M'

            return { categories, purity, sorting, order, toprange }
        }

        let wpcPage = 1
        let wpcMeta = null
        let wpcLoading = false
        let wpcObserver = null

        let flickrPage = 1
        let flickrMeta = null
        let flickrLoading = false
        let flickrObserver = null

        const setupWallhavenObserver = () => {
            if (wallhavenObserver) {
                wallhavenObserver.disconnect()
                wallhavenObserver = null
            }

            if (!wallhavenMeta || wallhavenPage >= wallhavenMeta.last_page) {
                return
            }

            const sentinel = build('div')
            sentinel.className = 'wallhaven-sentinel'
            sentinel.style.height = '100px'
            sentinel.style.width = '100%'
            searchResults.appendChild(sentinel)

            wallhavenObserver = new IntersectionObserver(async (entries) => {
                if (entries[0].isIntersecting && !wallhavenLoading) {
                    wallhavenLoading = true
                    await loadMoreWallhavenResults()
                    wallhavenLoading = false
                }
            }, { rootMargin: '200px' })

            wallhavenObserver.observe(sentinel)
        }

        const loadMoreWallhavenResults = async () => {
            const searchTerm = qs('#searchinpt').value.trim().replace(/\s+/g, ' ')
            if (!searchTerm || wallhavenPage >= wallhavenMeta.last_page) return

            const params = getWallhavenParams()
            wallhavenPage++

            const queryParams = new URLSearchParams({
                src: 'wallhaven',
                page: wallhavenPage,
                categories: params.categories,
                purity: params.purity,
                sorting: params.sorting,
                order: params.order,
                toprange: params.toprange
            })

            try {
                const raw = await fetchIgscrapDebounced('/api/igscrap/' + encodeURIComponent(searchTerm) + '?' + queryParams.toString())
                const data = await raw.json()

                if (Array.isArray(data.results)) {
                    for (let entry of data.results) {
                        const fullImageUrl = entry.url
                        const thumbUrl = entry.thumb
                        if (!fullImageUrl || !thumbUrl) continue

                        const img = build('img')
                        img.src = "/api/igserve/" + btoa(thumbUrl)

                        img.addEventListener('error', () => img.remove())
                        img.addEventListener('click', () => {
                            if (!loading) loadImg(fullImageUrl, thumbUrl)
                        })
                        searchResults.appendChild(img)
                    }

                    if (data.meta) {
                        wallhavenMeta = data.meta
                    }

                    setupWallhavenObserver()
                }
            } catch (e) {
                console.error('Failed to load more wallhaven results:', e)
            }
        }

        const setupWallpaperscraftObserver = () => {
            if (wpcObserver) {
                wpcObserver.disconnect()
                wpcObserver = null
            }

            if (!wpcMeta || wpcPage >= wpcMeta.last_page) {
                return
            }

            const sentinel = build('div')
            sentinel.className = 'wpc-sentinel'
            sentinel.style.height = '100px'
            sentinel.style.width = '100%'
            searchResults.appendChild(sentinel)

            wpcObserver = new IntersectionObserver(async (entries) => {
                if (entries[0].isIntersecting && !wpcLoading) {
                    wpcLoading = true
                    await loadMoreWallpaperscraftResults()
                    wpcLoading = false
                }
            }, { rootMargin: '200px' })

            wpcObserver.observe(sentinel)
        }

        const loadMoreWallpaperscraftResults = async () => {
            const searchTerm = qs('#searchinpt').value.trim().replace(/\s+/g, ' ')
            if (!searchTerm || wpcPage >= wpcMeta.last_page) return

            wpcPage++

            const queryParams = new URLSearchParams({
                src: 'wallpaperscraft',
                page: wpcPage
            })

            try {
                const raw = await fetchIgscrapDebounced('/api/igscrap/' + encodeURIComponent(searchTerm) + '?' + queryParams.toString())
                const data = await raw.json()

                if (Array.isArray(data.results)) {
                    for (let entry of data.results) {
                        const fullImageUrl = entry.url
                        const thumbUrl = entry.thumb
                        if (!fullImageUrl || !thumbUrl) continue

                        const img = build('img')
                        img.src = "/api/igserve/" + btoa(thumbUrl)

                        img.addEventListener('error', () => img.remove())
                        img.addEventListener('click', () => {
                            if (!loading) loadImg(fullImageUrl, thumbUrl)
                        })
                        searchResults.appendChild(img)
                    }

                    if (data.meta) {
                        wpcMeta = data.meta
                    }

                    setupWallpaperscraftObserver()
                }
            } catch (e) {
                console.error('Failed to load more wallpaperscraft results:', e)
            }
        }

        const setupFlickrObserver = () => {
            if (flickrObserver) {
                flickrObserver.disconnect()
                flickrObserver = null
            }

            if (!flickrMeta || flickrPage >= flickrMeta.last_page) {
                return
            }

            const sentinel = build('div')
            sentinel.className = 'flickr-sentinel'
            sentinel.style.height = '100px'
            sentinel.style.width = '100%'
            searchResults.appendChild(sentinel)

            flickrObserver = new IntersectionObserver(async (entries) => {
                if (entries[0].isIntersecting && !flickrLoading) {
                    flickrLoading = true
                    await loadMoreFlickrResults()
                    flickrLoading = false
                }
            }, { rootMargin: '200px' })

            flickrObserver.observe(sentinel)
        }

        const loadMoreFlickrResults = async () => {
            const searchTerm = qs('#searchinpt').value.trim().replace(/\s+/g, ' ')
            if (!searchTerm || flickrPage >= flickrMeta.last_page) return

            flickrPage++

            const queryParams = new URLSearchParams({
                src: 'flickr',
                page: flickrPage
            })

            try {
                const raw = await fetchIgscrapDebounced('/api/igscrap/' + encodeURIComponent(searchTerm) + '?' + queryParams.toString())
                const data = await raw.json()

                if (Array.isArray(data.results)) {
                    for (let entry of data.results) {
                        const fullImageUrl = entry.url
                        const thumbUrl = entry.thumb
                        if (!fullImageUrl || !thumbUrl) continue

                        const img = build('img')
                        img.src = "/api/igserve/" + btoa(thumbUrl)

                        img.addEventListener('error', () => img.remove())
                        img.addEventListener('click', () => {
                            if (!loading) loadImg(fullImageUrl, thumbUrl)
                        })
                        searchResults.appendChild(img)
                    }

                    if (data.meta) {
                        flickrMeta = data.meta
                    }

                    setupFlickrObserver()
                }
            } catch (e) {
                console.error('Failed to load more flickr results:', e)
            }
        }

        async function search() {
            const searchTerm = qs('#searchinpt').value.trim().replace(/\s+/g, ' ')
            const selectedSource = sourceSelect?.value || 'wallhaven'
            const requestId = ++searchRequestId

            if (searchController) {
                searchController.abort()
            }

            if (!searchTerm) {
                searchResults.innerHTML = ''
                return
            }

            searchController = new AbortController()
            renderSearchLoading()

            wallhavenPage = 1
            wallhavenMeta = null
            if (wallhavenObserver) {
                wallhavenObserver.disconnect()
                wallhavenObserver = null
            }

            wpcPage = 1
            wpcMeta = null
            if (wpcObserver) {
                wpcObserver.disconnect()
                wpcObserver = null
            }

            flickrPage = 1
            flickrMeta = null
            if (flickrObserver) {
                flickrObserver.disconnect()
                flickrObserver = null
            }

            try {
                let queryParams = '?src=' + encodeURIComponent(selectedSource)

                if (selectedSource === 'wallhaven') {
                    const params = getWallhavenParams()
                    queryParams += '&page=1&categories=' + params.categories + '&purity=' + params.purity + '&sorting=' + params.sorting + '&order=' + params.order + '&toprange=' + params.toprange
                } else if (selectedSource === 'wallpaperscraft' || selectedSource === 'flickr') {
                    queryParams += '&page=1'
                }

                const raw = await fetchIgscrapDebounced('/api/igscrap/' + encodeURIComponent(searchTerm) + queryParams, {
                    signal: searchController.signal
                })
                const data = await raw.json()

                if (requestId !== searchRequestId) {
                    return
                }

                searchResults.innerHTML = ''

                let results = data
                if ((selectedSource === 'wallhaven' || selectedSource === 'wallpaperscraft' || selectedSource === 'flickr') && data.results) {
                    results = data.results
                    if (selectedSource === 'wallhaven') wallhavenMeta = data.meta
                    else if (selectedSource === 'wallpaperscraft') wpcMeta = data.meta
                    else flickrMeta = data.meta
                }

                if (!Array.isArray(results)) {
                    renderSearchMessage(results && results.error ? results.error : 'search failed')
                    return
                }

                for (let entry of results) {
                    const fullImageUrl = typeof entry === 'string' ? entry : entry.url
                    const thumbUrl = typeof entry === 'string' ? entry : entry.thumb
                    if (!fullImageUrl || !thumbUrl) {
                        continue
                    }

                    const img = build('img')
                    img.src = "/api/igserve/" + btoa(thumbUrl)

                    img.addEventListener('error', () => {
                        img.remove()
                    })
            
                    img.addEventListener('click', () => {
                        if (!loading) {
                            loadImg(fullImageUrl, thumbUrl)
                        }
                    })
                    searchResults.appendChild(img)
                }

                if (!results.length) {
                    renderSearchMessage('no results')
                    return
                }

                if (selectedSource === 'wallhaven') {
                    setupWallhavenObserver()
                } else if (selectedSource === 'wallpaperscraft') {
                    setupWallpaperscraftObserver()
                }
            } catch (error) {
                if (error && error.name === 'AbortError') {
                    return
                }
                if (requestId !== searchRequestId) {
                    return
                }
                renderSearchMessage('search failed')
            }
        }


       

        const debouncedSearch = debounce(() => {
            search()
        }, 450)

        qs('#searchinpt').addEventListener('input', debouncedSearch)
        if (sourceSelect) {
            sourceSelect.addEventListener('change', () => {
                const selectedSource = sourceSelect.value
                const searchResults = qs('#searchresults')
                const localFileArea = qs('#localfilearea')
                const searchBarWrap = qs('.searchbarwrap')
                
                if (selectedSource === 'local') {
                    searchResults.style.display = 'none'
                    localFileArea.style.display = 'block'
                    searchBarWrap.style.display = 'none'
                    wallhavenControls.style.display = 'none'
                } else {
                    searchResults.style.display = 'block'
                    localFileArea.style.display = 'none'
                    searchBarWrap.style.display = 'block'
                    
                    wallhavenControls.style.display = selectedSource === 'wallhaven' ? 'block' : 'none'
                    
                    search()
                }
            })
        }

        const setupWallhavenControls = () => {
            const whSorting = qs('#wh-sorting')
            const whToprangeGroup = qs('#wh-toprange-group')

            if (whSorting) {
                whSorting.addEventListener('change', () => {
                    if (whToprangeGroup) {
                        whToprangeGroup.style.display = whSorting.value === 'toplist' ? 'block' : 'none'
                    }
                    debouncedSearch()
                })
            }

            const whFilters = qsa('#wallhaven-controls input, #wallhaven-controls select')
            whFilters.forEach(el => {
                el.addEventListener('change', debouncedSearch)
            })
        }
        setupWallhavenControls()

        if (store) {
            store.addEventListener('click',async () => {
            if (isFxProcessing) {
                showTransientToast('Wait for processing to finish')
                return
            }
            if (!stageimg.complete || !stageimg.width || !stageimg.height) {
                window.alert('Load an image before storing it.')
                return
            }
            if (!hasFxApplied()) {
                window.alert('Apply at least one FX before uploading.')
                return
            }
            const uploadImage = buildUploadPngDataUrl(outcanvas)
            if (uploadImage.bytes > IGSTORE_TARGET_MAX_BYTES) {
                window.alert('Image is still too large to upload. Reduce resolution and try again.')
                return
            }

            const imageHash = await hashImageData(uploadImage.dataUrl)
            if (uploadedImageHashes.has(imageHash)) {
                showTransientToast('Duplicate upload blocked')
                return
            }
            const raw = await fetch('/api/igstore',{
                method:'post',
                headers:{
                    "Content-Type":"application/json"
                },
                body:JSON.stringify({data:uploadImage.dataUrl, fxCount:qsa('#fxbox .fxhold').length})
            })
            const resp=await raw.json()
            if (!raw.ok) {
                window.alert(resp.message || 'Upload failed')
                return
            }
            uploadedImageHashes.add(imageHash)
            persistUploadedHashes()
            showTransientToast(uploadImage.resized ? 'Upload succeeded (resized)' : 'Upload succeeded')
            })
        }


        qs('#save').addEventListener('click', () => {
            if (!stageimg.complete || !stageimg.width || !stageimg.height) {
                window.alert('Load an image before saving.')
                return
            }
            // Prefer saving to the local bucket (IndexedDB via legacy-bucket-integration).
            // Falls back to a PNG download if the bucket isn't available.
            const bucket = _unstaglitchBucket && _unstaglitchBucket.bucket
            if (bucket) {
                const fxCount = qsa('#fxbox .fxhold').length
                const name = `unstaglitch_${fxCount > 0 ? fxCount + 'fx_' : ''}${Date.now()}.png`
                outcanvas.toBlob((blob) => {
                    if (!blob) {
                        showTransientToast('Save failed: no image data')
                        return
                    }
                    blob.arrayBuffer().then(data => {
                        bucket.add({
                            source: 'unstaglitch',
                            name,
                            imageData: data,
                            imageFormat: 'image/png',
                            imageMime: 'image/png',
                            fileType: 'image'
                        }).then(() => {
                            showTransientToast('Saved to bucket')
                        }).catch(() => {
                            showTransientToast('Save to bucket failed')
                        })
                    }).catch(() => showTransientToast('Save to bucket failed'))
                }, 'image/png')
                return
            }
            // Fallback: download PNG
            const image = outcanvas.toDataURL("image/png").replace("image/png", "image/octet-stream"); // here is the most important part because if you dont replace you will get a DOM 18 exception.
            let u = document.createElement('a')
            u.setAttribute('href', image)
            u.download = "unstaglitch_" + Date.now() + '.png';
            u.addEventListener('click', () => {
                u = null
            })
            var clickEvent = new MouseEvent("click", {
                "view": window,
                "bubbles": true,
                "cancelable": false
            });
            u.dispatchEvent(clickEvent);
        })

        const buildctrl = (fx) => {

         
            if (sortable) {
                sortable.destroy()
                sortable = null
            }

      
            const holdbox = build('li')
          
            holdbox.classList.add('fxhold')
            const inputbox = build('div')


            inputbox.classList.add('fxbox')
         

            //inputbox.classList.add(fx.name)

            inputbox.dataset.ison = "true"

            const handle = build('div')
            handle.classList.add('handle')


            handle.innerHTML = fx.name
            const handle2 = build('div')
            handle2.classList.add('topfx')
            const switchid = uuid()
            const onoff = build('input')
            onoff.id = switchid
            onoff.checked = true


            onoff.classList.add('switch')
            onoff.type = 'checkbox'
            const label = build('label')
            label.classList.add('switchlabel')
            label.setAttribute("for", switchid)


            //<input type="checkbox" class="switch" /><label class="switchlabel" for="switch"></label>


            const delbox=build('div')
            delbox.classList.add('btnborder')
            const del = build('button')
            del.classList.add('btnctrl')
            del.classList.add('icn')

            del.innerHTML = ''
            delbox.appendChild(del)
            const boxhold = build('div')
            boxhold.classList.add('fxcontent')
            boxhold.dataset.uid='_' + Math.random().toString(36).substr(2, 9)
            boxhold.dataset.type=fx.name

            handle2.appendChild(onoff)
            handle2.appendChild(label)
            handle2.appendChild(delbox)
            handle.appendChild(handle2)

            inputbox.appendChild(handle)
            inputbox.appendChild(boxhold)

            const ctrlgrid = build('div')
            ctrlgrid.classList.add('ctrlgrid')
            boxhold.appendChild(ctrlgrid)

            let eleminputs = []
            for (let inpt of fx.inputs) {



                switch (inpt.type) {
                    case "knob":
                        
                        const input = build('input')
                        input.type = "number"
                        input.classList.add('ctrlinput')
                      
                        input.dataset.label = inpt.name
                        input.dataset.nocurve = "true"
                        input.dataset.nomenu = "true"
                        input.dataset.uuid = `${inpt.name}-${inputbox.dataset.id}`
                        input.classList.add(boxhold.dataset.uid)
                        input.min = inpt.min
                        input.max = inpt.max
                        input.step = inpt.step
                        input.value = inpt.value
                        const div = build('div')
                        div.classList.add('knobholder')
                        ctrlgrid.appendChild(div)
                        div.appendChild(input)
                    
                        eleminputs[inpt.name] = input
                        input.addEventListener('change', () => {
                            debouncedRenderStageImage()
                         })
                        ;(async () => { await faderH(input) })()
                        break
                    case "select":
                        const selbox = build('div')

                        selbox.classList.add('select')
                        const selinput = build('select')
                        selinput.classList.add(boxhold.dataset.uid)
                        selinput.classList.add('ctrlinput')
                        for (let opt of inpt.options) {
                            const option = build('option')
                            option.innerHTML = opt.name.toString().replace(/([A-Z])/g, ' $1')
                            option.value = opt.value
                            selinput.appendChild(option)
                        }
                        ctrlgrid.appendChild(selbox)
                        selbox.appendChild(selinput)
                        eleminputs[inpt.name] = selinput
                        selinput.addEventListener('change', () => {
                            debouncedRenderStageImage()
                         })
                        break
                }
                // const input = build('input')
                // input.type = "number"
                // input.classList.add('ctrlinput')
                // input.dataset.name = inpt.name
                // input.dataset.uuid = `${inpt.name}-${inputbox.dataset.id}`
                // in
                // input.min = inpt.min
                // input.max = inpt.max
                // input.step = inpt.step
                // input.value = inpt.value
             
                // inputbox.appendChild(input)
                // // fader(input, {
                // //     orientation: 'horizontal'
                // // })
                // ctrlz.knob(input)
                // eleminputs[inpt.name] = input
            }

            holdbox.appendChild(inputbox)
            qs('#fxbox').appendChild(holdbox)
            sortable = Sortable.create(qs('#fxbox'), {
                handle: ".handle",
                helper: "clone",
                axis: "y",
                onEnd: (/**Event*/evt) => {

                   renderStageImage()

                }
            })
            del.addEventListener('click', (e) => {

                holdbox.remove()
               if (!hasFxApplied()) {
                   drawStagePlaceholder()
                   if (stageimg.complete && stageimg.width && stageimg.height) {
                       renderStageImage()
                   }
                   return
               }
               renderStageImage()
            })
           renderStageImage()


        }


        qs('#clearfx').addEventListener('click', () => {
         
            if (sortable) {
                sortable.destroy()
                sortable = null
            }
            qs('#fxbox').innerHTML = ""
            window.faderLibs = []
        
            if (stageimg.complete && stageimg.width && stageimg.height) {
                renderStageImage()
                return
            }
            drawStagePlaceholder()
        })

        qs('#randomize').addEventListener('click', () => {
            const fxControls = qsa('#fxbox .fxhold input[type="number"].ctrlinput')
            fxControls.forEach(input => {
                const rangeInput = input
                const min = parseFloat(rangeInput.min)
                const max = parseFloat(rangeInput.max)
                const step = parseFloat(rangeInput.step) || 1
                
                if (!isNaN(min) && !isNaN(max)) {
                    const steps = Math.floor((max - min) / step)
                    const randomStep = Math.floor(Math.random() * (steps + 1))
                    const newValue = min + (randomStep * step)
                    
                    rangeInput.value = newValue
                    rangeInput.dispatchEvent(new Event('randomize', { bubbles: true }))
                }
            })
        })

     



  
        qs('#output').addEventListener('click', () => {
            renderStageImage()
        })

        // Local file handling
        const localFileInput = qs('#localfileinput')
        const localFileDropzone = qs('#localfiledropzone')
        
        // Click to select file
        localFileDropzone.addEventListener('click', () => {
            localFileInput.click()
        })
        
        // Handle file selection
        localFileInput.addEventListener('change', (e) => {
            const file = e.target.files[0]
            if (file && file.type.startsWith('image/')) {
                loadLocalFile(file)
            }
        })
        
        // Drag and drop functionality
        localFileDropzone.addEventListener('dragover', (e) => {
            e.preventDefault()
            localFileDropzone.classList.add('dragover')
        })
        
        localFileDropzone.addEventListener('dragleave', (e) => {
            e.preventDefault()
            localFileDropzone.classList.remove('dragover')
        })
        
        localFileDropzone.addEventListener('drop', (e) => {
            e.preventDefault()
            localFileDropzone.classList.remove('dragover')
            
            const file = e.dataTransfer.files[0]
            if (file && file.type.startsWith('image/')) {
                loadLocalFile(file)
            }
        })
        
        function loadLocalFile(file) {
            const reader = new FileReader()
            reader.onload = (e) => {
                const dataUrl = e.target.result
                originalimg.onload = function() {
                    originalimg.onerror = null
                    stageimg.src = normalizeToTargetResolution(originalimg)
                }
                originalimg.onerror = function() {
                    originalimg.onerror = null
                    window.alert('Failed to load the selected image.')
                }
                originalimg.src = dataUrl
            }
            reader.onerror = function() {
                window.alert('Failed to read the selected file.')
            }
            reader.readAsDataURL(file)
        }

        // Expose loadLocalFile so the bucket drop handler can use it
        window.__unstaglitchLoadFile = loadLocalFile

        if (sourceSelect) {
            sourceSelect.dispatchEvent(new Event('change'))
        }

        search()


    })

// ----------------------------------------------------------------------------
// Bucket integration — drag glitched output to bucket, drag images from bucket
// ----------------------------------------------------------------------------
const _unstaglitchBucket = initLegacyBucket({
    appName: 'unstaglitch',
    onBucketDrop: (e) => {
        // Check for output canvas drag
        const isCanvas = e.dataTransfer.getData('application/x-unstaglitch-canvas')
        if (isCanvas && _unstaglitchBucket.bucket) {
            const canvas = qs('#outputcanvas')
            if (canvas) {
                canvas.toBlob((blob) => {
                    if (!blob) return
                    blob.arrayBuffer().then(data => {
                        _unstaglitchBucket.bucket.add({
                            source: 'unstaglitch',
                            name: `unstaglitch_${Date.now()}.png`,
                            imageData: data,
                            imageFormat: 'image/png',
                            imageMime: 'image/png',
                            fileType: 'image'
                        }).catch(() => {})
                    })
                }, 'image/png')
            }
            return
        }
        // Check for search result image drag
        const searchImgUrl = e.dataTransfer.getData('application/x-unstaglitch-search-img')
        if (searchImgUrl && _unstaglitchBucket.bucket) {
            const name = e.dataTransfer.getData('text/plain') || `search_${Date.now()}.jpg`
            _unstaglitchBucket.bucket.setStatus(`Fetching ${name}...`)
            // Fetch via the igserve proxy (same URL the img src uses)
            fetch(searchImgUrl)
                .then(r => r.blob())
                .then(blob => blob.arrayBuffer())
                .then(data => {
                    const ext = (name.split('.').pop() || 'jpg').toLowerCase()
                    const mime = {
                        png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
                        gif: 'image/gif', bmp: 'image/bmp', webp: 'image/webp'
                    }[ext] || 'image/jpeg'
                    _unstaglitchBucket.bucket.add({
                        source: 'unstaglitch',
                        name,
                        imageData: data,
                        imageFormat: mime,
                        imageMime: mime,
                        fileType: 'image'
                    }).catch(err => {
                        _unstaglitchBucket.bucket.setStatus('Failed to add image')
                    })
                })
                .catch(err => {
                    _unstaglitchBucket.bucket.setStatus('Failed to fetch image')
                })
            return
        }
    },
})

// Make search result images draggable to bucket
// A MutationObserver watches #searchresults and adds dragstart to each new <img>
setTimeout(() => {
    const searchResults = qs('#searchresults')
    if (!searchResults) return

    function patchImg(img) {
        if (img._bucketDragPatched) return
        img._bucketDragPatched = true
        img.draggable = true
        img.addEventListener('dragstart', (e) => {
            // The img src is /api/igserve/<base64-of-thumb-or-full-url>
            // We want to fetch the full-resolution image for the bucket.
            // The click handler uses loadImg(fullImageUrl, thumbUrl) but we
            // can't access those closures. Instead, use the img.src (igserve proxy)
            // which serves the image at whatever resolution igserve provides.
            const src = img.src
            if (!src) return
            e.dataTransfer.effectAllowed = 'copy'
            e.dataTransfer.setData('application/x-unstaglitch-search-img', src)
            // Derive a name from the URL
            const parts = src.split('/')
            const last = parts[parts.length - 1] || ''
            const name = last ? decodeURIComponent(last).slice(0, 40) + '.jpg' : `search_${Date.now()}.jpg`
            e.dataTransfer.setData('text/plain', name)
            // Small drag image
            try {
                const c = document.createElement('canvas')
                c.width = 80
                c.height = 80
                const ctx = c.getContext('2d')
                ctx.drawImage(img, 0, 0, 80, 80)
                e.dataTransfer.setDragImage(c, 40, 40)
            } catch (_) {}
        })
    }

    // Patch existing images
    searchResults.querySelectorAll('img').forEach(patchImg)

    // Watch for new images added by search/infinite scroll
    const observer = new MutationObserver((mutations) => {
        for (const mut of mutations) {
            for (const node of mut.addedNodes) {
                if (node.tagName === 'IMG') patchImg(node)
                else if (node.querySelectorAll) node.querySelectorAll('img').forEach(patchImg)
            }
        }
    })
    observer.observe(searchResults, { childList: true, subtree: true })
}, 1000)

// Make output canvas draggable to bucket
setTimeout(() => {
    const canvas = qs('#outputcanvas')
    if (canvas) {
        canvas.draggable = true
        canvas.addEventListener('dragstart', (e) => {
            e.dataTransfer.effectAllowed = 'copy'
            e.dataTransfer.setData('application/x-unstaglitch-canvas', '1')
            e.dataTransfer.setData('text/plain', 'unstaglitch output')
        })
    }
}, 500)

// Allow dropping bucket images and search result images onto the stage / dropzone
setTimeout(() => {
    if (!_unstaglitchBucket.bucket) return

    // Shared drop logic: handle bucket item OR search result image
    async function handleImageDrop(e) {
        // 1. Bucket image drop
        const itemId = e.dataTransfer.getData('application/x-bucket-item-id')
        if (itemId) {
            e.preventDefault()
            e.stopPropagation()
            try {
                const dbItems = await _unstaglitchBucket.dbGetAll(_unstaglitchBucket.bucket.bucketId)
                const item = dbItems.find(i => i.id === itemId)
                if (item && item.type === 'image' && item.wav) {
                    const ext = (item.name.split('.').pop() || 'png').toLowerCase()
                    const mime = {
                        png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
                        gif: 'image/gif', bmp: 'image/bmp', webp: 'image/webp'
                    }[ext] || 'image/png'
                    const blob = new Blob([item.wav], { type: mime })
                    const file = new File([blob], item.name, { type: mime })
                    if (typeof window.__unstaglitchLoadFile === 'function') {
                        window.__unstaglitchLoadFile(file)
                    }
                    return true
                }
            } catch (err) {
            }
            return false
        }

        // 2. Search result image drop — fetch via igserve proxy and load
        const searchImgUrl = e.dataTransfer.getData('application/x-unstaglitch-search-img')
        if (searchImgUrl) {
            e.preventDefault()
            e.stopPropagation()
            try {
                const res = await fetch(searchImgUrl)
                const blob = await res.blob()
                const file = new File([blob], `search_${Date.now()}.jpg`, { type: blob.type || 'image/jpeg' })
                if (typeof window.__unstaglitchLoadFile === 'function') {
                    window.__unstaglitchLoadFile(file)
                }
                return true
            } catch (err) {
            }
            return false
        }
        return false
    }

    // Accept both bucket items and search result images as valid drop types
    const VALID_DROP_TYPES = ['application/x-bucket-item-id', 'application/x-unstaglitch-search-img']

    function attachDropTarget(el) {
        if (!el || el._bucketDropPatched) return
        el._bucketDropPatched = true
        el.addEventListener('dragover', (e) => {
            if (VALID_DROP_TYPES.some(t => e.dataTransfer.types.includes(t))) {
                e.preventDefault()
                e.dataTransfer.dropEffect = 'copy'
                el.classList.add('dragover')
            }
        })
        el.addEventListener('dragleave', (e) => {
            if (e.target === el) el.classList.remove('dragover')
        })
        el.addEventListener('drop', (e) => {
            el.classList.remove('dragover')
            handleImageDrop(e)
        }, true)
    }

    // 1. Local file dropzone (in the controls panel)
    attachDropTarget(qs('#localfiledropzone'))

    // 2. Stage body (the render area containing #outputcanvas)
    attachDropTarget(qs('.stagebody'))

    // 3. Also the output canvas itself (in case stagebody doesn't catch it)
    attachDropTarget(qs('#outputcanvas'))
}, 500)